import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import useI18n from '../../../i18n/useI18n'
import { Actions as ApiCallActions } from '../../../redux/apiCall/reducers'
import RoleBasedAccess from '../../../components/role-based-access'
import { CommonAllOption, UserTypeEnum } from '../../../utils/enums'
import Table from '../../../components/table'
import Pagination from '../../../components/pagination'
import general from '../../../utils/general'
import FilterCard from '../../../components/filter-card'
import CommissionSummaryCard from './components/CommissionSummaryCard'
import PaymentFormModal from './components/PaymentFormModal'
import PaymentDetailModal from './components/PaymentDetailModal'
import CommissionDetailModal from './components/CommissionDetailModal'
import './details.scss'

const defaultPaymentForm = {
    amount: '',
    paymentDate: '',
    paymentMethod: '',
    status: '',
    referenceNumber: '',
    description: '',
    notes: ''
}

const commissionHistoryFilterDefaults = {
    paymentDateFrom: '',
    paymentDateTo: '',
    status: '',
    paymentMethod: ''
}

const paymentHistoryFilterDefaults = {
    createdAtFrom: '',
    createdAtTo: '',
    status: ''
}

const AgentCommissionDetail = () => {
    const dispatch = useDispatch()
    const { id } = useParams()
    const location = useLocation()
    const { t } = useI18n()

    const [activeTab, setActiveTab] = useState('commissions')
    const [commissionHistory, setCommissionHistory] = useState([])
    const [payments, setPayments] = useState([])
    const [commissionDetails, setCommissionDetails] = useState(null)
    const [loadingCommissionHistory, setLoadingCommissionHistory] = useState(true)
    const [loadingPayments, setLoadingPayments] = useState(true)
    const [loadingDetails, setLoadingDetails] = useState(true)
    const [paymentModalOpen, setPaymentModalOpen] = useState(false)
    const [paymentForm, setPaymentForm] = useState(defaultPaymentForm)
    const [editingPaymentId, setEditingPaymentId] = useState(null)
    const [paymentDetailModalOpen, setPaymentDetailModalOpen] = useState(false)
    const [commissionDetailModalOpen, setCommissionDetailModalOpen] = useState(false)
    const [selectedPayment, setSelectedPayment] = useState(null)
    const [selectedCommission, setSelectedCommission] = useState(null)
    const [commissionHistoryFilters, setCommissionHistoryFilters] = useState(commissionHistoryFilterDefaults)
    const [paymentHistoryFilters, setPaymentHistoryFilters] = useState(paymentHistoryFilterDefaults)
    const [commissionPagination, setCommissionPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    })
    const [paymentPagination, setPaymentPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    })

    const agentFromState = location?.state?.agent || {}

    useEffect(() => {
        if (!id) return
        fetchCommissionDetails()
        fetchCommissionHistory()
        fetchPayments()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const getAgentName = (agent = {}) => {
        if (agent.companyName) return agent.companyName
        if (agent.name) return agent.name
        const firstName = agent?.user?.firstName || agent?.firstName || ''
        const lastName = agent?.user?.lastName || agent?.lastName || ''
        const combined = `${firstName} ${lastName}`.trim()
        return combined || agent?.user?.email || agent?.email || t('AGENTS_ENTITY')
    }

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || amount === '') return '-'
        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
        if (Number.isNaN(numericAmount)) return amount
        const formattedAmount = new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(numericAmount)
        return `$${formattedAmount}`
    }

    const formatDate = (value) => {
        if (!value) return '-'
        return general.formatDate(value)
    }

    const normalizeStatus = (value) => {
        if (!value) return 'unknown'
        return value
            .toString()
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/-+/g, '_')
    }

    const getStatusLabel = (value) => {
        if (!value) return '-'
        const statusKeyMap = {
            pending: 'AGENT_COMMISSIONS_STATUS_PENDING',
            approved: 'AGENT_COMMISSIONS_STATUS_APPROVED',
            accepted: 'AGENT_COMMISSIONS_STATUS_APPROVED',
            rejected: 'AGENT_COMMISSIONS_STATUS_REJECTED',
            cancelled: 'AGENT_COMMISSIONS_STATUS_CANCELLED',
            canceled: 'AGENT_COMMISSIONS_STATUS_CANCELLED',
            paid: 'AGENT_COMMISSIONS_STATUS_PAID',
            unpaid: 'AGENT_COMMISSIONS_STATUS_UNPAID',
            active: 'AGENT_COMMISSIONS_STATUS_ACTIVE',
            inactive: 'AGENT_COMMISSIONS_STATUS_INACTIVE',
            completed: 'AGENT_COMMISSIONS_STATUS_COMPLETED'
        }
        const key = statusKeyMap[normalizeStatus(value)]
        return key ? t(key) : value
    }

    const commissionDetailData = useMemo(() => {
        if (!commissionDetails) return {}
        return commissionDetails.commission || commissionDetails.data || commissionDetails
    }, [commissionDetails])

    const earningsData = useMemo(() => {
        return commissionDetailData.earnings || {}
    }, [commissionDetailData])

    const agentData = useMemo(() => {
        return commissionDetails?.agent || commissionDetails?.agentInfo || agentFromState || {}
    }, [commissionDetails, agentFromState])

    const fetchCommissionDetails = () => {
        setLoadingDetails(true)
        dispatch(
            ApiCallActions.Get({
                url: `agents/${id}/commission-details`,
                onSuccess: ({ data }) => {
                    setCommissionDetails(data?.data || data || null)
                    setLoadingDetails(false)
                },
                onError: () => {
                    setCommissionDetails(null)
                    setLoadingDetails(false)
                }
            })
        )
    }

    const buildCommissionHistoryQuery = (filterParams = commissionHistoryFilters) => {
        const queryParams = {}

        const addQueryParam = (key, value) => {
            if (value === undefined || value === null) return
            if (typeof value === 'string') {
                const trimmed = value.trim()
                if (!trimmed) return
                queryParams[key] = trimmed
                return
            }
            queryParams[key] = value
        }

        addQueryParam('paymentDateFrom', filterParams.paymentDateFrom)
        addQueryParam('paymentDateTo', filterParams.paymentDateTo)
        addQueryParam('status', filterParams.status)
        addQueryParam('paymentMethod', filterParams.paymentMethod)

        return queryParams
    }

    const fetchCommissionHistory = (filterParams = commissionHistoryFilters) => {
        setLoadingCommissionHistory(true)
        dispatch(
            ApiCallActions.Get({
                url: `agents/${id}/commissions`,
                query: buildCommissionHistoryQuery(filterParams),
                onSuccess: ({ data }) => {
                    const rows = data?.data || data || []
                    setCommissionHistory(Array.isArray(rows) ? rows : [])
                    setLoadingCommissionHistory(false)
                },
                onError: () => {
                    setCommissionHistory([])
                    setLoadingCommissionHistory(false)
                }
            })
        )
    }

    const buildPaymentHistoryQuery = (filterParams = paymentHistoryFilters) => {
        const queryParams = {}

        const addQueryParam = (key, value) => {
            if (value === undefined || value === null) return
            if (typeof value === 'string') {
                const trimmed = value.trim()
                if (!trimmed) return
                queryParams[key] = trimmed
                return
            }
            queryParams[key] = value
        }

        addQueryParam('createdAtFrom', filterParams.createdAtFrom)
        addQueryParam('createdAtTo', filterParams.createdAtTo)
        addQueryParam('status', filterParams.status)

        return queryParams
    }

    const fetchPayments = (filterParams = paymentHistoryFilters) => {
        setLoadingPayments(true)
        dispatch(
            ApiCallActions.Get({
                url: `agents/${id}/payments`,
                query: buildPaymentHistoryQuery(filterParams),
                onSuccess: ({ data }) => {
                    const rows = data?.data || data || []
                    setPayments(Array.isArray(rows) ? rows : [])
                    setLoadingPayments(false)
                },
                onError: () => {
                    setPayments([])
                    setLoadingPayments(false)
                }
            })
        )
    }

    useEffect(() => {
        const total = payments.length
        const totalPages = Math.max(1, Math.ceil(total / paymentPagination.limit))
        setPaymentPagination((prev) => ({
            ...prev,
            total,
            totalPages,
            page: prev.page > totalPages ? totalPages : prev.page
        }))
    }, [payments, paymentPagination.limit])

    useEffect(() => {
        const total = commissionHistory.length
        const totalPages = Math.max(1, Math.ceil(total / commissionPagination.limit))
        setCommissionPagination((prev) => ({
            ...prev,
            total,
            totalPages,
            page: prev.page > totalPages ? totalPages : prev.page
        }))
    }, [commissionHistory.length, commissionPagination.limit])

    const handleCommissionFiltersChange = (newFilters) => {
        setCommissionHistoryFilters(newFilters)
    }

    const handleCommissionFiltersSubmit = (newFilters) => {
        setCommissionHistoryFilters(newFilters)
        setCommissionPagination((prev) => ({
            ...prev,
            page: 1
        }))
        fetchCommissionHistory(newFilters)
    }

    const handleCommissionFiltersClear = (clearedFilters) => {
        setCommissionHistoryFilters(clearedFilters)
        setCommissionPagination((prev) => ({
            ...prev,
            page: 1
        }))
        fetchCommissionHistory(clearedFilters)
    }

    const openNewPaymentModal = () => {
        setEditingPaymentId(null)
        setPaymentForm(defaultPaymentForm)
        setPaymentModalOpen(true)
    }

    const openEditPaymentModal = (payment) => {
        const paymentId = payment?.id || payment?.paymentId || payment?._id
        setEditingPaymentId(paymentId || null)
        setPaymentForm({
            amount: payment?.amount !== undefined && payment?.amount !== null ? String(payment.amount) : '',
            paymentDate: payment?.paymentDate || payment?.date || '',
            paymentMethod: payment?.paymentMethod || payment?.method || '',
            status: payment?.status || payment?.state || '',
            referenceNumber: payment?.referenceNumber || '',
            description: payment?.description || '',
            notes: payment?.notes || ''
        })
        setPaymentModalOpen(true)
    }

    const openPaymentDetailModal = (payment) => {
        setSelectedPayment(payment || null)
        setPaymentDetailModalOpen(true)
    }

    const openCommissionDetailModal = (commission) => {
        setSelectedCommission(commission || null)
        setCommissionDetailModalOpen(true)
    }

    const handlePaymentChange = (e) => {
        const { name, value } = e.target
        setPaymentForm((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const handlePaymentSubmit = (e) => {
        e.preventDefault()
        if (!paymentForm.amount || !paymentForm.paymentDate || !paymentForm.paymentMethod) {
            general.notificationError(t('AGENT_COMMISSIONS_PAYMENT_REQUIRED'))
            return
        }

        const submitData = {
            amount: parseFloat(paymentForm.amount),
            paymentDate: paymentForm.paymentDate,
            paymentMethod: paymentForm.paymentMethod
        }
        const isEditing = Boolean(editingPaymentId)
        general.addOptionalFields(submitData, paymentForm, {
            string: isEditing
                ? ['referenceNumber', 'description', 'notes', 'status']
                : ['referenceNumber', 'description', 'notes']
        })
        dispatch(
            (isEditing ? ApiCallActions.Put : ApiCallActions.Post)({
                url: isEditing
                    ? `agents/${id}/payments/${editingPaymentId}`
                    : `agents/${id}/payments`,
                data: submitData,
                showAlertOnError: true,
                showLoading: true,
                onSuccess: () => {
                    general.messageSuccess(
                        t(isEditing ? 'AGENT_COMMISSIONS_PAYMENT_SUCCESS_UPDATE' : 'AGENT_COMMISSIONS_PAYMENT_SUCCESS_CREATE')
                    )
                    setPaymentModalOpen(false)
                    fetchPayments()
                }
            })
        )
    }

    const paymentMethodOptions = [
        { value: 'bank_transfer', label: t('AGENT_COMMISSIONS_PAYMENT_METHOD_BANK_TRANSFER') },
        { value: 'cash', label: t('AGENT_COMMISSIONS_PAYMENT_METHOD_CASH') },
        { value: 'credit_card', label: t('AGENT_COMMISSIONS_PAYMENT_METHOD_CREDIT_CARD') },
        { value: 'other', label: t('AGENT_COMMISSIONS_PAYMENT_METHOD_OTHER') }
    ]

    const paymentStatusOptions = [
        { value: 'pending', label: t('AGENT_COMMISSIONS_STATUS_PENDING') },
        { value: 'completed', label: t('AGENT_COMMISSIONS_STATUS_COMPLETED') },
        { value: 'cancelled', label: t('AGENT_COMMISSIONS_STATUS_CANCELLED') }
    ]

    const getPaymentMethodLabel = (value) => {
        if (!value) return '-'
        return paymentMethodOptions.find((option) => option.value === value)?.label || value
    }

    const handlePaymentPageChange = (page) => {
        setPaymentPagination((prev) => ({
            ...prev,
            page
        }))
    }

    const handlePaymentLimitChange = (newLimit) => {
        const totalPages = Math.max(1, Math.ceil(payments.length / newLimit))
        setPaymentPagination((prev) => ({
            ...prev,
            limit: newLimit,
            page: 1,
            totalPages
        }))
    }

    const handlePaymentFiltersChange = (newFilters) => {
        setPaymentHistoryFilters(newFilters)
    }

    const handlePaymentFiltersSubmit = (newFilters) => {
        setPaymentHistoryFilters(newFilters)
        setPaymentPagination((prev) => ({
            ...prev,
            page: 1
        }))
        fetchPayments(newFilters)
    }

    const handlePaymentFiltersClear = (clearedFilters) => {
        setPaymentHistoryFilters(clearedFilters)
        setPaymentPagination((prev) => ({
            ...prev,
            page: 1
        }))
        fetchPayments(clearedFilters)
    }

    const handleCommissionPageChange = (page) => {
        setCommissionPagination((prev) => ({
            ...prev,
            page
        }))
    }

    const handleCommissionLimitChange = (newLimit) => {
        const totalPages = Math.max(1, Math.ceil(commissionHistory.length / newLimit))
        setCommissionPagination((prev) => ({
            ...prev,
            limit: newLimit,
            page: 1,
            totalPages
        }))
    }

    const paginatedPayments = useMemo(() => {
        const startIndex = (paymentPagination.page - 1) * paymentPagination.limit
        const endIndex = startIndex + paymentPagination.limit
        return payments.slice(startIndex, endIndex)
    }, [payments, paymentPagination.page, paymentPagination.limit])

    const paginatedCommissions = useMemo(() => {
        const startIndex = (commissionPagination.page - 1) * commissionPagination.limit
        const endIndex = startIndex + commissionPagination.limit
        return commissionHistory.slice(startIndex, endIndex)
    }, [commissionHistory, commissionPagination.page, commissionPagination.limit])

    const commissionColumns = [
        {
            key: 'date',
            label: t('AGENT_COMMISSIONS_TABLE_DATE'),
            render: (_, row) => formatDate(row.date || row.commissionDate || row.createdAt)
        },
        {
            key: 'student',
            label: t('AGENT_COMMISSIONS_TABLE_STUDENT'),
            render: (_, row) => {
                const student = row.student || row.application?.student || {}
                const name = `${student.firstName || ''} ${student.lastName || ''}`.trim()
                return name || student.user?.email || '-'
            }
        },
        {
            key: 'program',
            label: t('AGENT_COMMISSIONS_TABLE_PROGRAM'),
            render: (_, row) => row.program?.name || row.application?.program?.name || '-'
        },
        {
            key: 'commissionAmount',
            label: t('AGENT_COMMISSIONS_TABLE_COMMISSION_AMOUNT'),
            render: (_, row) => formatCurrency(row.commissionAmount || row.amount || row.totalAmount, row.currency)
        },
        {
            key: 'commissionRate',
            label: t('AGENT_COMMISSIONS_TABLE_COMMISSION_RATE'),
            render: (_, row) => `${row.commissionRate || '-'}%`
        },
        {
            key: 'programFee',
            label: t('AGENT_COMMISSIONS_TABLE_PROGRAM_FEE'),
            render: (_, row) => formatCurrency(row.programFee || row.program?.discountedFee || row.program?.tuitionFee, row.currency)
        },
        {
            key: 'status',
            label: t('AGENT_COMMISSIONS_TABLE_STATUS'),
            render: (_, row) => {
                const rawStatus = row.status || row.state
                const normalized = normalizeStatus(rawStatus)
                const label = getStatusLabel(rawStatus)
                return (
                    <span className={`status-badge status-${normalized}`}>
                        {label}
                    </span>
                )
            }
        }
    ]

    const commissionHistoryFilterFields = [
        {
            type: 'input',
            name: 'paymentDateFrom',
            placeholder: t('AGENT_COMMISSIONS_FILTERS_PAYMENT_DATE_FROM'),
            icon: 'zmdi zmdi-calendar-note',
            inputType: 'date'
        },
        {
            type: 'input',
            name: 'paymentDateTo',
            placeholder: t('AGENT_COMMISSIONS_FILTERS_PAYMENT_DATE_TO'),
            icon: 'zmdi zmdi-calendar-check',
            inputType: 'date'
        },
        {
            type: 'select',
            name: 'status',
            placeholder: t('AGENT_COMMISSIONS_FILTERS_STATUS_PLACEHOLDER'),
            icon: 'zmdi zmdi-info-outline',
            options: [
                CommonAllOption,
                { value: 'pending', label: t('AGENT_COMMISSIONS_STATUS_PENDING') },
                { value: 'completed', label: t('AGENT_COMMISSIONS_STATUS_COMPLETED') },
                { value: 'cancelled', label: t('AGENT_COMMISSIONS_STATUS_CANCELLED') }
            ]
        },
        {
            type: 'select',
            name: 'paymentMethod',
            placeholder: t('AGENT_COMMISSIONS_FILTERS_PAYMENT_METHOD_PLACEHOLDER'),
            icon: 'zmdi zmdi-card',
            options: [
                CommonAllOption,
                { value: 'bank_transfer', label: t('AGENT_COMMISSIONS_PAYMENT_METHOD_BANK_TRANSFER') },
                { value: 'cash', label: t('AGENT_COMMISSIONS_PAYMENT_METHOD_CASH') },
                { value: 'check', label: t('AGENT_COMMISSIONS_PAYMENT_METHOD_CHECK') },
                { value: 'other', label: t('AGENT_COMMISSIONS_PAYMENT_METHOD_OTHER') }
            ]
        }
    ]

    const paymentHistoryFilterFields = [
        {
            type: 'input',
            name: 'createdAtFrom',
            placeholder: t('AGENT_COMMISSIONS_FILTERS_CREATED_AT_FROM'),
            icon: 'zmdi zmdi-calendar-note',
            inputType: 'date'
        },
        {
            type: 'input',
            name: 'createdAtTo',
            placeholder: t('AGENT_COMMISSIONS_FILTERS_CREATED_AT_TO'),
            icon: 'zmdi zmdi-calendar-check',
            inputType: 'date'
        },
        {
            type: 'select',
            name: 'status',
            placeholder: t('AGENT_COMMISSIONS_FILTERS_STATUS_PLACEHOLDER'),
            icon: 'zmdi zmdi-info-outline',
            options: [
                CommonAllOption,
                { value: 'pending', label: t('AGENT_COMMISSIONS_STATUS_PENDING') },
                { value: 'completed', label: t('AGENT_COMMISSIONS_STATUS_COMPLETED') },
                { value: 'cancelled', label: t('AGENT_COMMISSIONS_STATUS_CANCELLED') }
            ]
        }
    ]

    const paymentColumns = [
        {
            key: 'paymentDate',
            label: t('AGENT_COMMISSIONS_TABLE_DATE'),
            render: (_, row) => formatDate(row.paymentDate || row.date)
        },
        {
            key: 'amount',
            label: t('AGENT_COMMISSIONS_TABLE_AMOUNT'),
            render: (_, row) => formatCurrency(row.amount, row.currency || commissionDetailData.currency)
        },
        {
            key: 'paymentMethod',
            label: t('AGENT_COMMISSIONS_TABLE_PAYMENT_METHOD'),
            render: (_, row) => getPaymentMethodLabel(row.paymentMethod || row.method)
        },
        {
            key: 'status',
            label: t('AGENT_COMMISSIONS_TABLE_STATUS'),
            render: (_, row) => {
                const rawStatus = row.status
                const normalized = normalizeStatus(rawStatus)
                const label = getStatusLabel(rawStatus)
                return (
                    <span className={`status-badge status-${normalized}`}>
                        {label}
                    </span>
                )
            }
        },
        {
            key: 'referenceNumber',
            label: t('AGENT_COMMISSIONS_TABLE_REFERENCE'),
            render: (_, row) => row.referenceNumber || '-'
        },
        {
            key: 'description',
            label: t('AGENT_COMMISSIONS_TABLE_DESCRIPTION'),
            render: (_, row) => row.description || '-'
        },
        {
            key: 'notes',
            label: t('AGENT_COMMISSIONS_TABLE_NOTES'),
            render: (_, row) => row.notes || '-'
        }
    ]

    return (
        <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
            <section className="content agent-commission-detail">
                <div className="block-header">
                    <div className="row">
                        <div className="col-lg-7 col-md-6 col-sm-12 text-left">
                            <h2>
                                {t('AGENT_COMMISSIONS_DETAIL_TITLE')}
                                <small>{t('AGENT_COMMISSIONS_DETAIL_SUBTITLE')}</small>
                            </h2>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-12">
                            <ul className="breadcrumb float-md-right">
                                <li className="breadcrumb-item">
                                    <a href="/dashboard">
                                        <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i>
                                        {t('COMMON_HOME')}
                                    </a>
                                </li>
                                <li className="breadcrumb-item">
                                    <a href="/agent-commissions">{t('AGENT_COMMISSIONS_TITLE')}</a>
                                </li>
                                <li className="breadcrumb-item active">{getAgentName(agentData)}</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="container-fluid">
                    <div className="row clearfix">
                        <div className="col-lg-12">
                            <CommissionSummaryCard
                                loading={loadingDetails}
                                agentName={getAgentName(agentData)}
                                agentData={agentData}
                                earningsData={earningsData}
                                commissionDetailData={commissionDetailData}
                                t={t}
                                formatCurrency={formatCurrency}
                                formatDate={formatDate}
                            />
                        </div>
                    </div>

                    <div className="row clearfix">
                        <div className="col-lg-12">
                            <div className="card commission-tabs-card">
                                <ul className="nav nav-tabs">
                                    <li className="nav-item">
                                        <a
                                            className={`nav-link ${activeTab === 'commissions' ? 'active' : ''}`}
                                            href="#commissions"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                setActiveTab('commissions')
                                            }}
                                        >
                                            {t('AGENT_COMMISSIONS_TAB_COMMISSIONS')}
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <a
                                            className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
                                            href="#payments"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                setActiveTab('payments')
                                            }}
                                        >
                                            {t('AGENT_COMMISSIONS_TAB_PAYMENTS')}
                                        </a>
                                    </li>
                                </ul>

                                <div className="tab-content">
                                    <div className={`tab-pane body ${activeTab === 'commissions' ? 'active' : ''}`} id="commissions">
                                        <FilterCard
                                            filters={commissionHistoryFilters}
                                            onFilterChange={handleCommissionFiltersChange}
                                            onFilterSubmit={handleCommissionFiltersSubmit}
                                            onClearFilters={handleCommissionFiltersClear}
                                            loading={loadingCommissionHistory}
                                            title="AGENT_COMMISSIONS_FILTERS_TITLE"
                                            titleHide="COMMON_FILTERS"
                                            titleShow="COMMON_FILTERS"
                                            fields={commissionHistoryFilterFields}
                                            defaultFilters={commissionHistoryFilterDefaults}
                                            defaultOpen={false}
                                        />
                                        <Table
                                            columns={commissionColumns}
                                            data={paginatedCommissions}
                                            loading={loadingCommissionHistory}
                                            emptyMessage={t('AGENT_COMMISSIONS_COMMISSION_HISTORY_EMPTY')}
                                            showAutoId={true}
                                            pagination={commissionPagination}
                                            renderActions={(row) => (
                                                <div className="d-flex align-items-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-info btn-icon btn-round m-r-5"
                                                        onClick={() => openCommissionDetailModal(row)}
                                                        title={t('AGENT_COMMISSIONS_ACTIONS_DETAIL')}
                                                    >
                                                        <i className="zmdi zmdi-eye"></i>
                                                    </button>
                                                </div>
                                            )}
                                        />
                                        {commissionHistory.length > 0 && (
                                            <Pagination
                                                currentPage={commissionPagination.page}
                                                totalPages={commissionPagination.totalPages}
                                                total={commissionPagination.total}
                                                limit={commissionPagination.limit}
                                                onPageChange={handleCommissionPageChange}
                                                onLimitChange={handleCommissionLimitChange}
                                            />
                                        )}
                                    </div>

                                    <div className={`tab-pane body ${activeTab === 'payments' ? 'active' : ''}`} id="payments">
                                        <FilterCard
                                            filters={paymentHistoryFilters}
                                            onFilterChange={handlePaymentFiltersChange}
                                            onFilterSubmit={handlePaymentFiltersSubmit}
                                            onClearFilters={handlePaymentFiltersClear}
                                            loading={loadingPayments}
                                            title="AGENT_COMMISSIONS_FILTERS_TITLE"
                                            titleHide="COMMON_FILTERS"
                                            titleShow="COMMON_FILTERS"
                                            fields={paymentHistoryFilterFields}
                                            defaultFilters={paymentHistoryFilterDefaults}
                                            defaultOpen={false}
                                        />
                                        <div className="payments-header">
                                            <button
                                                style={{display: 'flex', alignItems: 'flex-end'}}
                                                type="button"
                                                className="btn btn-primary btn-round"
                                                onClick={openNewPaymentModal}
                                            >
                                                <i className="zmdi zmdi-plus mr-2"></i>
                                                {t('AGENT_COMMISSIONS_PAYMENT_ADD')}
                                            </button>
                                        </div>
                                        <Table
                                            columns={paymentColumns}
                                            data={paginatedPayments}
                                            loading={loadingPayments}
                                            emptyMessage={t('AGENT_COMMISSIONS_PAYMENT_EMPTY')}
                                            showAutoId={true}
                                            pagination={paymentPagination}
                                            renderActions={(row) => (
                                                <div className="d-flex align-items-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-info btn-icon btn-round m-r-5"
                                                        onClick={() => openPaymentDetailModal(row)}
                                                        title={t('AGENT_COMMISSIONS_ACTIONS_DETAIL')}
                                                    >
                                                        <i className="zmdi zmdi-eye"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-warning btn-icon btn-round m-r-5"
                                                        onClick={() => openEditPaymentModal(row)}
                                                        title={t('AGENT_COMMISSIONS_PAYMENT_EDIT')}
                                                    >
                                                        <i className="zmdi zmdi-edit"></i>
                                                    </button>
                                                </div>
                                            )}
                                        />
                                        {payments.length > 0 && (
                                            <Pagination
                                                currentPage={paymentPagination.page}
                                                totalPages={paymentPagination.totalPages}
                                                total={paymentPagination.total}
                                                limit={paymentPagination.limit}
                                                onPageChange={handlePaymentPageChange}
                                                onLimitChange={handlePaymentLimitChange}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <PaymentFormModal
                isOpen={paymentModalOpen}
                onClose={() => setPaymentModalOpen(false)}
                onSubmit={handlePaymentSubmit}
                formData={paymentForm}
                onChange={handlePaymentChange}
                paymentMethodOptions={paymentMethodOptions}
                paymentStatusOptions={paymentStatusOptions}
                isEditing={Boolean(editingPaymentId)}
                t={t}
                agentName={getAgentName(agentData)}
            />
            <PaymentDetailModal
                isOpen={paymentDetailModalOpen}
                onClose={() => setPaymentDetailModalOpen(false)}
                payment={selectedPayment}
                agentName={getAgentName(agentData)}
                t={t}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                getPaymentMethodLabel={getPaymentMethodLabel}
                currency={commissionDetailData.currency}
            />
            <CommissionDetailModal
                isOpen={commissionDetailModalOpen}
                onClose={() => setCommissionDetailModalOpen(false)}
                commission={selectedCommission}
                agentName={getAgentName(agentData)}
                t={t}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
            />
        </RoleBasedAccess>
    )
}

export default AgentCommissionDetail

